import { useEffect, useState, useSyncExternalStore } from 'react';
import { storage, REDUCE_MOTION_STORAGE_KEY } from '@adrodd/core';

const QUERY = '(prefers-reduced-motion: reduce)';

// Module-level store (not a Context/Provider) so every call site — the
// reader settings toggle and providers.tsx's MotionConfig at the app root,
// which don't share a component subtree — reads and writes the same live
// value via useSyncExternalStore, the same way `storage` itself is a single
// source of truth. Mirrors useReaderPalette.ts's persistence key convention.
let settingState = storage.getItem(REDUCE_MOTION_STORAGE_KEY) === 'true';
const listeners = new Set<() => void>();
function setReduceMotion(next: boolean) {
  settingState = next;
  storage.setItem(REDUCE_MOTION_STORAGE_KEY, String(next));
  listeners.forEach((l) => l());
}
function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
function getSnapshot() {
  return settingState;
}

/**
 * "Reduce motion" reading setting. `effective` (setting OR the OS's own
 * prefers-reduced-motion, live-subscribed) drives `[data-motion="reduced"]`
 * on <html>, which globals.css turns into a blanket animation/transition
 * kill switch, and feeds framer-motion's MotionConfig at the app root.
 */
export function useReducedMotion(): {
  reduceMotion: boolean;
  setReduceMotion: (b: boolean) => void;
  effective: boolean;
} {
  const reduceMotion = useSyncExternalStore(subscribe, getSnapshot);
  const [osReduced, setOsReduced] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.(QUERY).matches,
  );

  useEffect(() => {
    const mql = window.matchMedia(QUERY);
    const onChange = () => setOsReduced(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const effective = reduceMotion || osReduced;
  useEffect(() => {
    document.documentElement.setAttribute('data-motion', effective ? 'reduced' : 'normal');
  }, [effective]);

  return { reduceMotion, setReduceMotion, effective };
}
